import type { RenderContext, VideoFrame } from '../../types';
import { WebGLShaderProgram } from '../../gpu/shader';
import { BaseEffect } from '../base';
import { VERTEX_FULLSCREEN, FRAG_VIGNETTE } from '../../shaders/glsl';
import { withOverlay } from '../environmental/composite';

const DEFAULTS = { amount: 0.55, softness: 0.55, roundness: 0.7 };

let vigProg: WebGLShaderProgram | null = null;

export class VignetteEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'vignette', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!vigProg) vigProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_VIGNETTE);
    return this.runShaderPass(frame, vigProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_amount', params.amount);
      prog.setUniform1f('u_softness', params.softness);
      prog.setUniform1f('u_roundness', params.roundness);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, _context: RenderContext): VideoFrame {
    return withOverlay(frame, (ctx, w, h) => {
      const cx = w * 0.5;
      const cy = h * 0.5;
      const rx = (w * 0.5) * (0.85 + params.roundness * 0.2);
      const ry = h * 0.5;
      const soft = 0.25 + params.softness * 0.55;
      const g = ctx.createRadialGradient(cx, cy, Math.min(rx, ry) * (1 - soft), cx, cy, Math.max(rx, ry) * 1.15);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(1, `rgba(0,0,0,${Math.min(0.92, params.amount * 0.75)})`);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    }, 'source-over');
  }
}
